import type { VideoBackgroundEffect } from '../types/meeting.types.ts';

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number
) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  const videoRatio = vw / vh;
  const rectRatio = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = vw;
  let sh = vh;

  if (videoRatio > rectRatio) {
    sw = vh * rectRatio;
    sx = (vw - sw) / 2;
  } else {
    sh = vw / rectRatio;
    sy = (vh - sh) / 2;
  }

  ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawDecorativeFrame(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  effect: VideoBackgroundEffect,
  mirrored: boolean
) {
  ctx.clearRect(0, 0, width, height);
  ctx.save();

  if (mirrored) {
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
  }

  if (effect === 'transparent') {
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 0.92;
    drawCover(ctx, video, 0, 0, width, height);
    ctx.globalAlpha = 1;
    ctx.restore();
    return;
  }

  if (effect === 'blur') {
    ctx.filter = 'blur(32px) brightness(0.85)';
    ctx.drawImage(video, -width * 0.2, -height * 0.2, width * 1.4, height * 1.4);
    ctx.filter = 'none';
    const pad = width * 0.06;
    drawCover(ctx, video, pad, pad, width - pad * 2, height - pad * 2);
    ctx.restore();
    return;
  }

  const gradient = ctx.createLinearGradient(0, 0, width, height);
  if (effect === 'purple') {
    gradient.addColorStop(0, '#7c3aed');
    gradient.addColorStop(0.5, '#4c1d95');
    gradient.addColorStop(1, '#0f172a');
  } else {
    gradient.addColorStop(0, '#2563eb');
    gradient.addColorStop(0.5, '#1e3a8a');
    gradient.addColorStop(1, '#0f172a');
  }

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  const frameW = width * 0.82;
  const frameH = height * 0.82;
  const frameX = (width - frameW) / 2;
  const frameY = (height - frameH) / 2;

  ctx.save();
  roundRect(ctx, frameX, frameY, frameW, frameH, 18);
  ctx.clip();
  drawCover(ctx, video, frameX, frameY, frameW, frameH);
  ctx.restore();
  ctx.restore();
}

export type CanvasFallbackOutput = {
  stream: MediaStream;
  stop: () => void;
  usesSegmentation: false;
};

export function createCanvasFallbackStream(
  sourceVideo: HTMLVideoElement,
  effect: VideoBackgroundEffect,
  mirrored = true
): CanvasFallbackOutput | null {
  if (sourceVideo.videoWidth === 0 || sourceVideo.videoHeight === 0) return null;

  const canvas = document.createElement('canvas');
  canvas.width = 1280;
  canvas.height = 720;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let rafId = 0;
  const render = () => {
    if (sourceVideo.readyState >= 2) {
      drawDecorativeFrame(ctx, sourceVideo, canvas.width, canvas.height, effect, mirrored);
    }
    rafId = requestAnimationFrame(render);
  };
  render();

  const stream = canvas.captureStream(30);
  return {
    stream,
    usesSegmentation: false,
    stop: () => {
      cancelAnimationFrame(rafId);
      stream.getTracks().forEach((track) => track.stop());
    },
  };
}
